import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { PPC_FARM_COLLECTIONS } from '../types/ppcFarmTypes';

// Types for the Mailing Cost Dashboard
export interface MailingCostKPIs {
  totalDirectMailSpend: number;
  totalDataScrapeSpend: number;
  totalOtherCosts: number;
  grandTotalSpend: number;
}

export interface DirectMailSpendRow {
  cohort: string;
  dateMailed: string;
  quantitySent: number;
  mailingCost: number;
  costPerMailer: number;
}

export interface DataListCostRow {
  expenseDate: string;
  item: string;
  amount: number;
}

export interface MaterialsOtherRow {
  expenseDate: string;
  item: string;
  amount: number;
}

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

/**
 * PPC Farm Cost Service
 * Handles all data operations for the Mailing Cost Dashboard
 */
export class PpcFarmCostService {
  
  /**
   * Get KPI data for the specified date range
   */
  static async getKPIData(dateRange: DateRange): Promise<MailingCostKPIs> {
    try {
      const [totalDirectMailSpend, totalDataScrapeSpend, totalOtherCosts] = await Promise.all([
        this.getTotalDirectMailSpend(dateRange),
        this.getTotalDataScrapeSpend(dateRange),
        this.getTotalOtherCosts(dateRange)
      ]);

      return {
        totalDirectMailSpend,
        totalDataScrapeSpend,
        totalOtherCosts,
        grandTotalSpend: totalDirectMailSpend + totalDataScrapeSpend + totalOtherCosts
      };
    } catch (error) {
      console.error('Error fetching KPI data:', error);
      throw error;
    }
  }

  /**
   * Get total direct mail spend from mailing ledger
   */
  static async getTotalDirectMailSpend(dateRange: DateRange): Promise<number> {
    try {
      const mailingLedgerRef = collection(db, PPC_FARM_COLLECTIONS.MAILING_LEDGER);
      let q = query(
        mailingLedgerRef,
        where('mailingCost', '!=', null)
      );

      // Add date range filter if provided
      if (dateRange.start) {
        q = query(q, where('dateMailed', '>=', Timestamp.fromDate(dateRange.start)));
      }
      if (dateRange.end) {
        q = query(q, where('dateMailed', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const snapshot = await getDocs(q);
      let total = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.mailingCost && typeof data.mailingCost === 'number') {
          total += data.mailingCost;
        }
      });

      return total;
    } catch (error) {
      console.error('Error calculating total direct mail spend:', error);
      return 0;
    }
  }

  /**
   * Get total supporting costs from direct mail cost collection
   */
  static async getTotalSupportingCosts(dateRange: DateRange): Promise<number> {
    try {
      const directMailCostRef = collection(db, PPC_FARM_COLLECTIONS.DIRECT_MAIL_COST);
      let q = query(directMailCostRef);

      // Add date range filter if provided
      if (dateRange.start) {
        q = query(q, where('expenseDate', '>=', Timestamp.fromDate(dateRange.start)));
      }
      if (dateRange.end) {
        q = query(q, where('expenseDate', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const snapshot = await getDocs(q);
      let total = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        // Only count non-campaign costs as supporting costs
        if (!data.isCampaignCost && data.amount && typeof data.amount === 'number') {
          total += data.amount;
        }
      });

      return total;
    } catch (error) {
      console.error('Error calculating total supporting costs:', error);
      return 0;
    }
  }

  /**
   * Get total data scrape spend from direct mail cost collection
   */
  static async getTotalDataScrapeSpend(dateRange: DateRange): Promise<number> {
    try {
      const directMailCostRef = collection(db, PPC_FARM_COLLECTIONS.DIRECT_MAIL_COST);
      let q = query(
        directMailCostRef,
        where('item', '==', 'Data Scrape')
      );

      // Add date range filter if provided
      if (dateRange.start) {
        q = query(q, where('expenseDate', '>=', Timestamp.fromDate(dateRange.start)));
      }
      if (dateRange.end) {
        q = query(q, where('expenseDate', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const snapshot = await getDocs(q);
      let total = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.amount && typeof data.amount === 'number') {
          total += data.amount;
        }
      });

      return total;
    } catch (error) {
      console.error('Error calculating total data scrape spend:', error);
      return 0;
    }
  }

  /**
   * Get total other costs from direct mail cost collection
   */
  static async getTotalOtherCosts(dateRange: DateRange): Promise<number> {
    try {
      const directMailCostRef = collection(db, PPC_FARM_COLLECTIONS.DIRECT_MAIL_COST);
      let q = query(
        directMailCostRef,
        where('isCampaignCost', '==', false)
      );

      // Add date range filter if provided
      if (dateRange.start) {
        q = query(q, where('expenseDate', '>=', Timestamp.fromDate(dateRange.start)));
      }
      if (dateRange.end) {
        q = query(q, where('expenseDate', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const snapshot = await getDocs(q);
      let total = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        // Only count non-data-scrape items as other costs
        if (data.item !== 'Data Scrape' && data.amount && typeof data.amount === 'number') {
          total += data.amount;
        }
      });

      return total;
    } catch (error) {
      console.error('Error calculating total other costs:', error);
      return 0;
    }
  }

  /**
   * Get direct mail spend breakdown data for Tab 1
   */
  static async getDirectMailSpendBreakdown(dateRange: DateRange): Promise<DirectMailSpendRow[]> {
    try {
      const mailingLedgerRef = collection(db, PPC_FARM_COLLECTIONS.MAILING_LEDGER);
      let q = query(
        mailingLedgerRef,
        where('mailingCost', '!=', null),
        orderBy('dateMailed', 'desc')
      );

      // Add date range filter if provided
      if (dateRange.start) {
        q = query(q, where('dateMailed', '>=', Timestamp.fromDate(dateRange.start)));
      }
      if (dateRange.end) {
        q = query(q, where('dateMailed', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const snapshot = await getDocs(q);
      const rows: DirectMailSpendRow[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.mailingCost && data.cohortQuantity && data.dateMailed) {
          const dateObj = data.dateMailed.toDate();
          const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
          const day = dateObj.getDate().toString().padStart(2, '0');
          const year = dateObj.getFullYear();
          
          rows.push({
            cohort: data.cohort || 'Unknown',
            dateMailed: `${month}/${day}/${year}`,
            quantitySent: data.cohortQuantity,
            mailingCost: data.mailingCost,
            costPerMailer: data.mailingCost / data.cohortQuantity
          });
        }
      });

      return rows;
    } catch (error) {
      console.error('Error fetching direct mail spend breakdown:', error);
      return [];
    }
  }

  /**
   * Get data & list costs for Tab 2
   */
  static async getDataListCosts(dateRange: DateRange): Promise<DataListCostRow[]> {
    try {
      const directMailCostRef = collection(db, PPC_FARM_COLLECTIONS.DIRECT_MAIL_COST);
      let q = query(
        directMailCostRef,
        where('item', '==', 'Data Scrape'),
        orderBy('expenseDate', 'desc')
      );

      // Add date range filter if provided
      if (dateRange.start) {
        q = query(q, where('expenseDate', '>=', Timestamp.fromDate(dateRange.start)));
      }
      if (dateRange.end) {
        q = query(q, where('expenseDate', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const snapshot = await getDocs(q);
      const rows: DataListCostRow[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        const dateObj = data.expenseDate.toDate();
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const day = dateObj.getDate().toString().padStart(2, '0');
        const year = dateObj.getFullYear();
        
        rows.push({
          expenseDate: `${month}/${day}/${year}`,
          item: data.item,
          amount: data.amount || 0
        });
      });

      return rows;
    } catch (error) {
      console.error('Error fetching data & list costs:', error);
      return [];
    }
  }

  /**
   * Get materials & other expenses for Tab 3
   */
  static async getMaterialsOtherExpenses(dateRange: DateRange): Promise<MaterialsOtherRow[]> {
    try {
      const directMailCostRef = collection(db, PPC_FARM_COLLECTIONS.DIRECT_MAIL_COST);
      let q = query(
        directMailCostRef,
        where('isCampaignCost', '==', false),
        orderBy('expenseDate', 'desc')
      );

      // Add date range filter if provided
      if (dateRange.start) {
        q = query(q, where('expenseDate', '>=', Timestamp.fromDate(dateRange.start)));
      }
      if (dateRange.end) {
        q = query(q, where('expenseDate', '<=', Timestamp.fromDate(dateRange.end)));
      }

      const snapshot = await getDocs(q);
      const rows: MaterialsOtherRow[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        // Exclude "Data Scrape" items as they go in Tab 2
        if (data.item !== 'Data Scrape') {
          const dateObj = data.expenseDate.toDate();
          const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
          const day = dateObj.getDate().toString().padStart(2, '0');
          const year = dateObj.getFullYear();
          
          rows.push({
            expenseDate: `${month}/${day}/${year}`,
            item: data.item,
            amount: data.amount || 0
          });
        }
      });

      return rows;
    } catch (error) {
      console.error('Error fetching materials & other expenses:', error);
      return [];
    }
  }

  /**
   * Utility method to create common date range presets
   */
  static getDateRangePresets(): { [key: string]: DateRange } {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    
    // Last Quarter calculation
    const lastQuarterStart = new Date(currentYear, (currentQuarter - 1) * 3, 1);
    const lastQuarterEnd = new Date(currentYear, currentQuarter * 3, 0);
    
    // This Year calculation
    const thisYearStart = new Date(currentYear, 0, 1);
    const thisYearEnd = new Date(currentYear, 11, 31);

    return {
      'Last Quarter': {
        start: lastQuarterStart,
        end: lastQuarterEnd
      },
      'This Year': {
        start: thisYearStart,
        end: thisYearEnd
      },
      'All Time': {
        start: null,
        end: null
      }
    };
  }

  /**
   * Format currency for display
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Format cost per mailer for display
   */
  static formatCostPerMailer(cost: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(cost);
  }
}

export default PpcFarmCostService;